import { OwnershipBeneficialOwner } from '@commenda/api';
import { Button, Dropdown, Icons } from '@commenda/kakashi';
import { CONTROL_TYPE_LABELS } from '../constants';

interface BeneficialOwnersListProps {
  beneficialOwners: OwnershipBeneficialOwner[];
  onViewKeyPerson: (directorId: number) => void;
  onEditControlType: (beneficialOwner: OwnershipBeneficialOwner) => void;
  onRemove: (beneficialOwner: OwnershipBeneficialOwner) => void;
  onAdd?: () => void;
}

const summarizeControl = (bor: OwnershipBeneficialOwner): string => {
  const effective = bor.directControlType ?? bor.paths[0]?.controlType ?? null;
  return effective ? (CONTROL_TYPE_LABELS[effective] ?? effective) : '—';
};

export const BeneficialOwnersList = ({
  beneficialOwners,
  onViewKeyPerson,
  onEditControlType,
  onRemove,
  onAdd,
}: BeneficialOwnersListProps) => (
  <div className="flex w-full flex-col gap-xl rounded-md border-[0.5px] border-border-primary bg-bg-primary p-xl shadow-[0px_2px_4px_rgba(0,0,0,0.04)]">
    <div className="flex items-center justify-between">
      <h3 className="txt-extra-small-bold text-text-primary">
        Ultimate Beneficial Owners
      </h3>
      {onAdd && (
        <Button variant="brand-link" size="sm" onClick={onAdd} type="button">
          <Icons.Plus className="size-4" />
          Add
        </Button>
      )}
    </div>

    {beneficialOwners.length === 0 ? (
      <p className="w-full txt-extra-small-regular text-text-secondary">
        No UBOs identified.
      </p>
    ) : (
      <ul className="flex w-full flex-col gap-md">
        {beneficialOwners.map((bor) => (
          <li
            key={bor.id}
            data-ai-item="Beneficial owner"
            data-ai-id={String(bor.id)}
            className="flex items-center gap-md rounded-sm border-[0.5px] border-border-primary bg-bg-primary p-md"
          >
            <button
              type="button"
              onClick={() => onViewKeyPerson(bor.directorId)}
              className="group flex min-w-0 flex-1 flex-col gap-xxs rounded-xs text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-fg-brand"
            >
              <span className="truncate txt-small-bold text-text-primary group-hover:underline">
                {bor.name}
              </span>
              <span className="txt-extra-small-regular text-text-secondary">
                {summarizeControl(bor)}
              </span>
            </button>

            <Dropdown
              align="end"
              menu={{
                groups: [
                  {
                    items: [
                      {
                        label: 'Edit control type',
                        icon: Icons.Edit02,
                        onClick: () => onEditControlType(bor),
                      },
                      {
                        label: 'End UBO role',
                        icon: Icons.Trash01,
                        labelClassName: 'text-text-error',
                        iconClassName: 'text-text-error',
                        onClick: () => onRemove(bor),
                      },
                    ],
                  },
                ],
              }}
            >
              <button
                type="button"
                aria-label={`More actions for ${bor.name}`}
                className="flex size-8 shrink-0 items-center justify-center rounded-xs hover:bg-bg-secondary"
              >
                <Icons.DotsVertical className="size-4 text-text-tertiary" />
              </button>
            </Dropdown>
          </li>
        ))}
      </ul>
    )}
  </div>
);
